'use client';

import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { useDesignLineContext } from '@/features/design/estimate';

interface AlbumDesignLineBannerProps {
  projectId: string;
  conceptId: string;
  disabled?: boolean;
  /** Generate the line's next blueprint version from this design. */
  onUpdate: (designLineId: string) => void;
}

/**
 * The design's blueprint line already has a blueprint built for another
 * design of the building: say so (API `album_status_text`) and offer to
 * update it for this one (docs/estimate/UI-SPEC-design-lines.md).
 */
export function AlbumDesignLineBanner({
  projectId,
  conceptId,
  disabled = false,
  onUpdate,
}: AlbumDesignLineBannerProps) {
  const t = useTranslations('common.designLine');
  const { data: context } = useDesignLineContext(projectId, conceptId);
  const lineId = context?.design_line_id;

  if (!context || !lineId || context.is_album_for_this_design || !context.line?.currentAlbum) {
    return null;
  }

  return (
    <NoticeBanner
      variant="info"
      message={context.album_status_text}
      action={
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onUpdate(lineId)}
        >
          {t('updateAlbumForDesign')}
        </Button>
      }
    />
  );
}
